import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import * as signalR from '@microsoft/signalr';
import { authFetch, getToken, isAdmin } from '../../auth';
import { hubUrl } from '../../appConfig';

const DEFAULT_MAP_CENTER = { lng: 114.173355, lat: 22.335468 };
const DEFAULT_MAP_ZOOM = 12;
const FOCUS_MAP_ZOOM = 15;
const DEVICE_PAGE_SIZE = 200;

export class GpsMonitor extends Component {
    static displayName = GpsMonitor.name;

    constructor(props) {
        super(props);
        this.initialFocus = this.readInitialFocus();
        this.pendingFocusDeviceNo = this.initialFocus.deviceNo || "";
        this.pendingFocusPosition = this.initialFocus.position;
        this.mapContainerRef = React.createRef();
        this.mapResizeTimers = [];
        this.isAdminUser = isAdmin();
        this.deviceScopeLoaded = false;
        this.deviceScopeRequestId = 0;
        this.state = {
            vehicles: [],
            hubConnection: null,
            isConnected: false,
            map: null,
            mapError: "",
            authError: "",
            customerError: "",
            customers: [],
            selectedCustomerId: this.isAdminUser ? this.initialFocus.customerId : "",
            visibleDeviceCount: 0,
            selectedDeviceNo: this.initialFocus.deviceNo || "",
            returnToAlarmLogs: this.initialFocus.returnTo || "",
            latestAlarm: null,
            isMapFullscreen: false
        };
        this.markers = {}; // 存储 DeviceNo -> Google Maps Marker 的引用
        this.infoWindows = {}; // 存储 DeviceNo -> InfoWindow 的引用
        this.allowedDeviceNos = new Set();
        this.fullscreenVehicleListControl = null;
        this.fullscreenVehicleListControlAttached = false;
    }

    readInitialFocus() {
        const qs = new URLSearchParams(window.location.search || "");
        const deviceNo = (qs.get("deviceNo") || "").trim();
        const longitude = Number(qs.get("longitude"));
        const latitude = Number(qs.get("latitude"));
        const customerId = (qs.get("customerId") || "").trim();
        const returnTo = this.normalizeAlarmLogReturnUrl(qs.get("returnTo") || "");
        const hasPosition = !Number.isNaN(longitude) && !Number.isNaN(latitude);

        return {
            deviceNo,
            position: hasPosition ? { lng: longitude, lat: latitude } : null,
            customerId,
            returnTo
        };
    }

    normalizeAlarmLogReturnUrl(value) {
        if (!value) return "";
        try {
            const url = new URL(value, window.location.origin);
            if (url.origin !== window.location.origin) return "";
            if (url.pathname !== "/alarm-logs") return "";
            return `${url.pathname}${url.search}`;
        } catch {
            return "";
        }
    }

    componentDidMount() {
        // 地图与实时连接解耦：即使地图加载失败，也可先看到实时列表
        this.loadCustomerOptions();
        this.loadVisibleDevices(this.state.selectedCustomerId).then(() => this.initSignalR());
        this.loadGoogleMapScript()
            .then(() => this.initMap())
            .catch((err) => this.setState({ mapError: err?.message || "地图脚本加载失败" }));
        window.addEventListener("resize", this.handleWindowResize);
        this.addFullscreenListeners();
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleWindowResize);
        this.removeFullscreenListeners();
        this.clearMapResizeTimers();
        if (this.state.hubConnection) {
            this.state.hubConnection.stop();
        }
        this.clearAllMarkers();
        this.detachFullscreenVehicleListControl();
    }

    componentDidUpdate(prevProps, prevState) {
        if (!this.state.map) return;

        if (prevState.visibleDeviceCount !== this.state.visibleDeviceCount || prevState.vehicles.length !== this.state.vehicles.length) {
            this.scheduleMapLayoutRefresh();
        }
    }

    clearAllMarkers() {
        Object.values(this.markers).forEach((marker) => {
            try {
                marker.setMap(null);
            } catch (e) {
                // ignore
            }
        });
        Object.values(this.infoWindows).forEach((info) => {
            try {
                info.close();
            } catch (e) {
                // ignore
            }
        });
        this.markers = {};
        this.infoWindows = {};
    }

    async loadCustomerOptions() {
        if (!this.isAdminUser) {
            return;
        }

        try {
            const res = await authFetch("/api/customers/options");
            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || "客户列表加载失败");
            }

            const data = await res.json();
            this.setState({
                customers: Array.isArray(data?.items) ? data.items : [],
                customerError: ""
            });
        } catch (err) {
            this.setState({ customerError: err?.message || "客户列表加载失败" });
        }
    }

    async loadVisibleDevices(customerId = "") {
        const requestId = ++this.deviceScopeRequestId;
        this.deviceScopeLoaded = false;
        try {
            const items = [];
            let page = 1;

            while (true) {
                const qs = new URLSearchParams({
                    page: String(page),
                    pageSize: String(DEVICE_PAGE_SIZE)
                });
                if (this.isAdminUser && customerId) {
                    qs.set("customerId", customerId);
                }

                const res = await authFetch(`/api/devices?${qs.toString()}`);
                if (!res.ok) {
                    const txt = await res.text();
                    throw new Error(txt || "设备列表加载失败");
                }

                const data = await res.json();
                const pageItems = Array.isArray(data?.items) ? data.items : [];
                items.push(...pageItems);

                const total = Number(data?.total || 0);
                if (items.length >= total || pageItems.length < DEVICE_PAGE_SIZE) {
                    break;
                }

                page += 1;
            }

            if (requestId !== this.deviceScopeRequestId) {
                return;
            }

            this.allowedDeviceNos = new Set(
                items
                    .map((x) => (x.deviceNo || "").toLowerCase())
                    .filter((x) => !!x)
            );
            this.deviceScopeLoaded = true;
            this.setState({ visibleDeviceCount: this.allowedDeviceNos.size, authError: "" });
        } catch (err) {
            if (requestId !== this.deviceScopeRequestId) {
                return;
            }
            this.allowedDeviceNos = new Set();
            this.deviceScopeLoaded = true;
            this.setState({ authError: err?.message || "加载可见设备失败" });
        }
    }

    isVisibleDevice(update) {
        const deviceNo = (update.deviceNo || update.deviceNO || "").toLowerCase();
        if (!deviceNo) return false;
        if (!this.deviceScopeLoaded) return false;
        if (this.allowedDeviceNos.size === 0) return false;
        return this.allowedDeviceNos.has(deviceNo);
    }

    handleCustomerChange = (e) => {
        const selectedCustomerId = e.target.value;
        this.clearAllMarkers();
        this.allowedDeviceNos = new Set();
        this.deviceScopeLoaded = false;
        this.pendingFocusDeviceNo = "";
        this.pendingFocusPosition = null;

        this.setState({
            selectedCustomerId,
            vehicles: [],
            visibleDeviceCount: 0,
            selectedDeviceNo: "",
            latestAlarm: null
        }, () => {
            this.loadVisibleDevices(selectedCustomerId);
            this.refreshMapLayout();
        });
    };

    // 1. 动态加载 Google 地图脚本
    loadGoogleMapScript() {
        return new Promise((resolve, reject) => {
            if (window.google && window.google.maps) {
                resolve();
                return;
            }

            const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
            if (!apiKey) {
                reject(new Error("未配置 REACT_APP_GOOGLE_MAPS_API_KEY，无法加载 Google 地图。"));
                return;
            }

            const callbackName = "__initGoogleMapCallback__";
            const script = document.createElement("script");
            script.src = `https://www.google.com.hk/maps/api/js?language=zh-CN&key=${encodeURIComponent(apiKey)}&callback=${callbackName}&v=3.exp&sensor=false`;
            script.async = true;
            script.onerror = () => {
                reject(new Error("Google 地图脚本下载失败，请检查网络和 API Key 限制。"));
            };

            const timer = setTimeout(() => {
                reject(new Error("Google 地图脚本加载超时。"));
            }, 15000);

            window[callbackName] = () => {
                clearTimeout(timer);
                delete window[callbackName];
                resolve();
            };

            document.head.appendChild(script);
        });
    }

    // 2. 初始化地图
    initMap() {
        if (!(window.google && window.google.maps)) {
            this.setState({ mapError: "Google 地图 SDK 未就绪。" });
            return;
        }

        const container = this.mapContainerRef.current;
        if (!container) {
            this.setState({ mapError: "地图容器未就绪。" });
            return;
        }

        const map = new window.google.maps.Map(container, {
            center: DEFAULT_MAP_CENTER,
            zoom: DEFAULT_MAP_ZOOM,
            gestureHandling: "greedy",
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true
        });

        this.attachFullscreenVehicleListControl(map);

        this.setState({ map }, () => {
            this.tryApplyInitialFocus();
            this.scheduleMapLayoutRefresh();
        });
    }

    clearMapResizeTimers() {
        this.mapResizeTimers.forEach((timer) => clearTimeout(timer));
        this.mapResizeTimers = [];
    }

    scheduleMapLayoutRefresh() {
        this.clearMapResizeTimers();
        [0, 200, 800].forEach((delay) => {
            const timer = setTimeout(() => this.refreshMapLayout(), delay);
            this.mapResizeTimers.push(timer);
        });
    }

    handleWindowResize = () => {
        this.refreshMapLayout();
    };

    addFullscreenListeners() {
        ["fullscreenchange", "webkitfullscreenchange", "mozfullscreenchange", "MSFullscreenChange"].forEach((eventName) => {
            document.addEventListener(eventName, this.handleFullscreenChange);
        });
    }

    removeFullscreenListeners() {
        ["fullscreenchange", "webkitfullscreenchange", "mozfullscreenchange", "MSFullscreenChange"].forEach((eventName) => {
            document.removeEventListener(eventName, this.handleFullscreenChange);
        });
    }

    getFullscreenElement() {
        return document.fullscreenElement
            || document.webkitFullscreenElement
            || document.mozFullScreenElement
            || document.msFullscreenElement
            || null;
    }

    handleFullscreenChange = () => {
        const fullscreenElement = this.getFullscreenElement();
        const mapContainer = this.mapContainerRef.current;
        const isMapFullscreen = Boolean(
            fullscreenElement
            && mapContainer
            && (
                fullscreenElement === mapContainer
                || fullscreenElement.contains(mapContainer)
                || mapContainer.contains(fullscreenElement)
            )
        );

        if (this.state.isMapFullscreen !== isMapFullscreen) {
            this.setState({ isMapFullscreen }, () => this.scheduleMapLayoutRefresh());
            return;
        }

        this.scheduleMapLayoutRefresh();
    };

    attachFullscreenVehicleListControl(map) {
        const controlPosition = window.google?.maps?.ControlPosition;
        const position = controlPosition?.LEFT_TOP || controlPosition?.TOP_LEFT;
        if (!map || position === undefined) return;

        if (!this.fullscreenVehicleListControl) {
            this.fullscreenVehicleListControl = document.createElement("div");
            this.fullscreenVehicleListControl.className = "monitor-fullscreen-vehicle-list-control";
        }

        if (this.fullscreenVehicleListControlAttached) return;

        map.controls[position].push(this.fullscreenVehicleListControl);
        this.fullscreenVehicleListControlAttached = true;
    }

    detachFullscreenVehicleListControl() {
        if (this.fullscreenVehicleListControl?.parentNode) {
            this.fullscreenVehicleListControl.parentNode.removeChild(this.fullscreenVehicleListControl);
        }

        this.fullscreenVehicleListControl = null;
        this.fullscreenVehicleListControlAttached = false;
    }

    focusVehicleOnMap(vehicle) {
        if (!this.state.map) return;

        const key = vehicle.deviceNo || vehicle.deviceNO;
        if (!key) return;

        const lng = Number(vehicle.longitude);
        const lat = Number(vehicle.latitude);
        if (Number.isNaN(lng) || Number.isNaN(lat)) return;

        this.setSelectedDevice(key);
        this.state.map.panTo({ lng, lat });
    }

    refreshMapLayout() {
        const { map, vehicles } = this.state;
        if (!map || vehicles.length > 0 || this.pendingFocusPosition) {
            if (map && window.google?.maps?.event) {
                window.google.maps.event.trigger(map, "resize");
            }
            return;
        }

        if (window.google?.maps?.event) {
            window.google.maps.event.trigger(map, "resize");
        }
        map.setCenter(DEFAULT_MAP_CENTER);
        map.setZoom(DEFAULT_MAP_ZOOM);
    }

    // 3. 初始化实时通讯
    initSignalR() {
        const token = getToken();
        // 使用同源路径，交给 CRA 代理转发到后端，避免端口/跨域导致连接失败
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl("/hubs/gps"), {
                accessTokenFactory: () => token
            })
            .withAutomaticReconnect()
            .build();

        connection.on("VehicleLocationUpdated", (update) => {
            if (!this.isVisibleDevice(update)) return;
            this.updateVehicleOnMap(update);
            this.updateVehicleInList(update);
        });

        connection.on("AlarmLogCreated", (alarm) => {
            if (!this.isVisibleDevice(alarm)) return;
            this.updateAlarmInList(alarm);
        });

        connection.on("AlarmLogUpdated", (alarm) => {
            if (!this.isVisibleDevice(alarm)) return;
            this.updateAlarmStateInList(alarm);
        });

        connection.start()
            .then(() => this.setState({ isConnected: true, hubConnection: connection }))
            .catch(err => console.error("SignalR Connection Error: ", err));
    }

    // 4. 核心：在地图上更新车辆
    updateVehicleOnMap(update) {
        const { map, selectedDeviceNo } = this.state;
        if (!map || !(window.google && window.google.maps)) return;

        const lng = Number(update.longitude);
        const lat = Number(update.latitude);
        if (Number.isNaN(lng) || Number.isNaN(lat)) return;

        const key = update.deviceNo || update.deviceNO;
        if (!key) return;
        const isSelected = selectedDeviceNo === key;

        const position = { lng, lat };
        const defaultIcon = "http://maps.google.com/mapfiles/ms/icons/red-dot.png";
        const selectedIcon = "http://maps.google.com/mapfiles/ms/icons/blue-dot.png";

        if (this.markers[key]) {
            this.markers[key].setPosition(position);
            this.markers[key].setIcon(isSelected ? selectedIcon : defaultIcon);
            if (this.infoWindows[key]) {
                this.infoWindows[key].setContent(
                    `<div><strong>${update.vehicleNo || key}</strong><br/>速度: ${Number(update.speed || 0).toFixed(1)}<br/>地址: ${update.address || "-"}</div>`
                );
            }
        } else {
            const marker = new window.google.maps.Marker({
                position,
                map,
                title: update.vehicleNo || key,
                icon: isSelected ? selectedIcon : defaultIcon
            });

            const info = new window.google.maps.InfoWindow({
                content: `<div><strong>${update.vehicleNo || key}</strong><br/>速度: ${Number(update.speed || 0).toFixed(1)}<br/>地址: ${update.address || "-"}</div>`
            });
            marker.addListener("click", () => {
                this.setSelectedDevice(key);
                info.open({ map, anchor: marker });
            });

            this.markers[key] = marker;
            this.infoWindows[key] = info;
        }

        if (isSelected && this.infoWindows[key]) {
            this.infoWindows[key].open({ map, anchor: this.markers[key] });
        }

        if (isSelected && this.pendingFocusDeviceNo === key) {
            map.panTo(position);
            if (map.getZoom() < FOCUS_MAP_ZOOM) {
                map.setZoom(FOCUS_MAP_ZOOM);
            }
            this.pendingFocusDeviceNo = "";
        }

        // 可选：当地图上只有一辆车时，自动跟随
        if (Object.keys(this.markers).length === 1) {
            map.panTo(position);
        }
    }

    tryApplyInitialFocus() {
        const { map, selectedDeviceNo, vehicles } = this.state;
        if (!map) return;

        if (this.pendingFocusPosition) {
            map.panTo(this.pendingFocusPosition);
            if (map.getZoom() < FOCUS_MAP_ZOOM) {
                map.setZoom(FOCUS_MAP_ZOOM);
            }
            this.pendingFocusPosition = null;
        }

        if (!selectedDeviceNo) return;

        const focusedVehicle = vehicles.find(v => (v.deviceNo || v.deviceNO) === selectedDeviceNo);
        if (!focusedVehicle) return;

        const lng = Number(focusedVehicle.longitude);
        const lat = Number(focusedVehicle.latitude);
        if (Number.isNaN(lng) || Number.isNaN(lat)) return;

        this.setSelectedDevice(selectedDeviceNo);
        map.panTo({ lng, lat });
        if (map.getZoom() < FOCUS_MAP_ZOOM) {
            map.setZoom(FOCUS_MAP_ZOOM);
        }
        this.pendingFocusDeviceNo = "";
    }

    setSelectedDevice(deviceNo) {
        this.setState({ selectedDeviceNo: deviceNo }, () => {
            Object.keys(this.markers).forEach((key) => {
                const marker = this.markers[key];
                if (!marker) return;
                const icon = key === deviceNo
                    ? "http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                    : "http://maps.google.com/mapfiles/ms/icons/red-dot.png";
                marker.setIcon(icon);
                if (this.infoWindows[key]) {
                    if (key === deviceNo) {
                        this.infoWindows[key].open({ map: this.state.map, anchor: marker });
                    } else {
                        this.infoWindows[key].close();
                    }
                }
            });
        });
    }

    updateVehicleInList(update) {
        this.setState(prevState => {
            const key = update.deviceNo || update.deviceNO;
            const index = prevState.vehicles.findIndex(v => (v.deviceNo || v.deviceNO) === key);
            let newVehicles = [...prevState.vehicles];
            if (index !== -1) {
                newVehicles[index] = {
                    ...newVehicles[index],
                    ...update
                };
            } else {
                newVehicles.push(update);
            }
            return { vehicles: newVehicles };
        }, () => {
            this.tryApplyInitialFocus();
        });
    }

    updateAlarmInList(alarm) {
        this.setState(prevState => {
            const key = alarm.deviceNo || alarm.deviceNO;
            const index = prevState.vehicles.findIndex(v => (v.deviceNo || v.deviceNO) === key);
            const nextVehicles = [...prevState.vehicles];

            const nextAlarmState = {
                lastAlarmId: alarm.id,
                lastAlarmType: alarm.alarmType,
                lastAlarmTypeText: alarm.alarmTypeText,
                lastAlarmTime: alarm.gpsTime,
                lastAlarmCleared: alarm.isCleared
            };

            if (index >= 0) {
                nextVehicles[index] = {
                    ...nextVehicles[index],
                    ...nextAlarmState
                };
            } else {
                nextVehicles.unshift({
                    deviceNo: key,
                    vehicleNo: alarm.vehicleNo,
                    longitude: alarm.longitude,
                    latitude: alarm.latitude,
                    speed: alarm.speed,
                    address: alarm.positionDesc,
                    gpsTime: alarm.gpsTime,
                    netState: 0,
                    ...nextAlarmState
                });
            }

            return {
                vehicles: nextVehicles,
                latestAlarm: alarm
            };
        }, () => {
            this.tryApplyInitialFocus();
        });
    }

    updateAlarmStateInList(alarm) {
        this.setState(prevState => {
            const key = alarm.deviceNo || alarm.deviceNO;
            const nextVehicles = prevState.vehicles.map(v => {
                if ((v.deviceNo || v.deviceNO) !== key) return v;
                if (v.lastAlarmId && v.lastAlarmId !== alarm.id) return v;
                return {
                    ...v,
                    lastAlarmId: alarm.id,
                    lastAlarmType: alarm.alarmType,
                    lastAlarmTypeText: alarm.alarmTypeText,
                    lastAlarmTime: alarm.gpsTime,
                    lastAlarmCleared: alarm.isCleared
                };
            });

            return {
                vehicles: nextVehicles,
                latestAlarm: prevState.latestAlarm && prevState.latestAlarm.id === alarm.id
                    ? { ...prevState.latestAlarm, ...alarm }
                    : prevState.latestAlarm
            };
        });
    }

    formatLocalDateTime(value) {
        if (!value) return "-";
        const dt = new Date(value);
        if (Number.isNaN(dt.getTime())) return "-";
        return dt.toLocaleString("zh-CN", { hour12: false });
    }

    returnToAlarmLogs() {
        if (!this.state.returnToAlarmLogs) return;
        window.location.assign(this.state.returnToAlarmLogs);
    }

    renderVehicleListCard(options = {}) {
        const { fullscreen = false, showConnectionStatus = true } = options;
        const bodyClassName = fullscreen
            ? "card-body p-0 monitor-fullscreen-vehicle-list-body"
            : "card-body p-0";
        const bodyStyle = fullscreen
            ? undefined
            : { maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' };

        return (
            <>
                <div className={`card shadow-sm status-card${fullscreen ? " monitor-fullscreen-list-card" : ""}`}>
                    <div className="card-header bg-primary text-white">
                        实时车辆列表 ({this.state.vehicles.length})
                    </div>
                    {this.isAdminUser && (
                        <div className="p-2 border-bottom">
                            <label className="form-label small text-muted mb-1">客户范围</label>
                            <select
                                className="form-select form-select-sm"
                                value={this.state.selectedCustomerId}
                                onChange={this.handleCustomerChange}
                            >
                                <option value="">全部客户</option>
                                {this.state.customers.map((customer) => (
                                    <option key={customer.id} value={String(customer.id)}>
                                        {customer.name}{customer.code ? ` / ${customer.code}` : ""} #{customer.id}
                                    </option>
                                ))}
                            </select>
                            {this.state.customerError && (
                                <div className="text-danger small mt-1">{this.state.customerError}</div>
                            )}
                        </div>
                    )}
                    {this.state.returnToAlarmLogs && (
                        <div className="p-2 border-bottom">
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm w-100"
                                onClick={() => this.returnToAlarmLogs()}
                            >
                                返回报警日志
                            </button>
                        </div>
                    )}
                    {this.state.latestAlarm && (
                        <div className="status-banner warn m-2">
                            最近报警：{this.state.latestAlarm.vehicleNo || this.state.latestAlarm.deviceNo} / {this.state.latestAlarm.alarmTypeText} / {this.formatLocalDateTime(this.state.latestAlarm.gpsTime)}
                            {this.state.latestAlarm.isCleared ? " / 已处理" : ""}
                        </div>
                    )}
                    <div className={bodyClassName} style={bodyStyle}>
                        <table className="table table-hover mb-0 monitor-vehicle-table">
                            <thead className="table-light">
                                <tr>
                                    <th>车牌</th>
                                    <th>速度</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.vehicles.length === 0 && (
                                    <tr>
                                        <td colSpan={2}>
                                            <div className="status-banner empty m-2">暂无实时车辆数据</div>
                                        </td>
                                    </tr>
                                )}
                                {this.state.vehicles.map(v => (
                                    <tr
                                        key={v.deviceNo || v.deviceNO}
                                        className={this.state.selectedDeviceNo === (v.deviceNo || v.deviceNO) ? "device-row-selected" : ""}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => this.focusVehicleOnMap(v)}
                                    >
                                        <td>
                                            <strong>{v.vehicleNo}</strong><br/>
                                            {v.lastAlarmTypeText && !v.lastAlarmCleared && (
                                                <>
                                                    <span className="badge bg-danger monitor-alarm-badge">{v.lastAlarmTypeText}</span><br/>
                                                </>
                                            )}
                                            <small className="text-muted" style={{fontSize: '10px'}}>{v.address}</small><br/>
                                            <small className="text-muted" style={{fontSize: '10px'}}>时间: {this.formatLocalDateTime(v.gpsTime)}</small>
                                        </td>
                                        <td><span className="badge bg-success">{Number(v.speed || 0).toFixed(1)}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {this.state.authError && (
                        <div className="status-banner warn m-2">
                            可见设备清单加载失败：{this.state.authError}
                        </div>
                    )}
                </div>
                {showConnectionStatus && (
                    <div className={`status-banner ${this.state.isConnected ? "info" : "warn"} mt-2`}>
                        已授权设备数：{this.state.visibleDeviceCount}；实时连接：{this.state.isConnected ? "已连接" : "未连接"}
                    </div>
                )}
            </>
        );
    }

    renderFullscreenVehicleListPortal() {
        if (!this.fullscreenVehicleListControl || !this.state.map) return null;

        return createPortal(
            <div className={`monitor-fullscreen-vehicle-panel${this.state.isMapFullscreen ? " show" : ""}`}>
                {this.renderVehicleListCard({ fullscreen: true, showConnectionStatus: true })}
            </div>,
            this.fullscreenVehicleListControl
        );
    }

    render() {
        return (
            <div className="container-fluid">
                <div className="row mt-3">
                    <div className="col-md-3">
                        {this.renderVehicleListCard()}
                    </div>
                    <div className="col-md-9">
                        {/* 地图容器保持空节点，交给 Google Maps SDK 管理 */}
                        <div style={{ position: 'relative', height: '750px', width: '100%' }}>
                            <div
                                id="mapContainer"
                                ref={this.mapContainerRef}
                                style={{ height: '100%', width: '100%', borderRadius: '8px', border: '1px solid #ddd' }}
                            />
                            {!this.state.map && (
                                <div
                                    className="text-center text-muted"
                                    style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    {this.state.mapError || "正在加载 Google 地图..."}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {this.renderFullscreenVehicleListPortal()}
            </div>
        );
    }
}
